import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Eye, Filter, Package, Tag, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/auth";
import { fmtMoney } from "../../../lib/currency";
import { Card, PageHeader, PrimaryButton, GhostButton } from "./_shared";

export default function AllProducts() {
  const { subdomain } = useParams();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState({ category_id: "all", brand_id: "all" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (filter.category_id && filter.category_id !== "all") params.category_id = filter.category_id;
      if (filter.brand_id && filter.brand_id !== "all") params.brand_id = filter.brand_id;
      const r = await api.get("/t/products", { params });
      setRows(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/t/categories").then((r) => setCats(r.data));
    api.get("/t/brands").then((r) => setBrands(r.data));
  }, []);
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/t/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const totalValue = rows.reduce((s, p) => s + (p.cost || 0) * (p.stock || 0), 0);
  const lowStock = rows.filter((p) => (p.stock || 0) <= (p.stock_alert || 0)).length;

  return (
    <div data-testid="products-page" className="p-6 lg:p-8">
      <PageHeader title="All Products" breadcrumb="Products · All Products"
        actions={<>
          <Link to={`/t/${subdomain}/products/import`}>
            <button className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold hover:border-black/25">
              Import CSV
            </button>
          </Link>
          <Link to={`/t/${subdomain}/products/new`}>
            <PrimaryButton icon={Plus} testid="products-new">Create Product</PrimaryButton>
          </Link>
        </>} />

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { lbl: "Total Products", v: rows.length, c: "text-[#1FA868]", bg: "bg-[#E8F5E9]", icon: Package, t: "stat-total-products" },
          { lbl: "Categories", v: cats.length, c: "text-violet-600", bg: "bg-violet-50", icon: Layers, t: "stat-categories" },
          { lbl: "Low Stock", v: lowStock, c: "text-rose-600", bg: "bg-rose-50", icon: Tag, t: "stat-low-stock" },
          { lbl: "Stock Value", v: fmtMoney(totalValue), c: "text-amber-600", bg: "bg-amber-50", icon: Package, t: "stat-stock-value" },
        ].map((s) => {
          const I = s.icon;
          return (
            <Card key={s.lbl} className="p-4">
              <div className="flex items-center gap-3" data-testid={s.t}>
                <div className={`w-10 h-10 rounded-xl ${s.bg} ${s.c} flex items-center justify-center`}><I size={16} /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-black/55 font-semibold">{s.lbl}</div>
                  <div className="font-display font-extrabold text-lg text-black">{s.v}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-3 flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-[#FAFBFB] rounded-lg px-3 py-2 flex-1 min-w-[260px]">
          <Search size={16} className="text-black/40" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, code, SKU…" data-testid="products-search"
            className="flex-1 bg-transparent focus:outline-none text-sm" />
          <button onClick={load} className="text-xs font-bold text-[#1FA868] hover:underline">Search</button>
        </div>
        <select value={filter.category_id} onChange={(e) => setFilter({ ...filter, category_id: e.target.value })}
          data-testid="products-filter-category"
          className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm font-semibold focus:border-[#1FA868] outline-none">
          <option value="all">All categories</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filter.brand_id} onChange={(e) => setFilter({ ...filter, brand_id: e.target.value })}
          data-testid="products-filter-brand"
          className="px-3 py-2 rounded-lg border border-black/15 bg-white text-sm font-semibold focus:border-[#1FA868] outline-none">
          <option value="all">All brands</option>
          {brands.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Card>

      <Card>

{/* DESKTOP TABLE */}
<div className="hidden md:block overflow-x-auto">
  <table className="w-full text-sm">
    <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase tracking-wider">
      <tr>
        <th className="text-left p-3.5 font-bold">Product</th>
        <th className="text-left p-3.5 font-bold">Code / SKU</th>
        <th className="text-left p-3.5 font-bold">Category / Brand</th>
        <th className="text-left p-3.5 font-bold">Cost</th>
        <th className="text-left p-3.5 font-bold">Price</th>
        <th className="text-left p-3.5 font-bold">Stock</th>
        <th className="text-left p-3.5 font-bold">Status</th>
        <th className="text-right p-3.5 font-bold">Actions</th>
      </tr>
    </thead>

    <tbody className="divide-y divide-black/5">
      {rows.map((p) => {
        const low = (p.stock || 0) <= (p.stock_alert || 0);

        return (
          <tr key={p.id} className="hover:bg-[#FAFBFB]">

            <td className="p-3.5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#E8F5E9] flex items-center justify-center overflow-hidden">
                  {p.image_url
                    ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.image_url}`} className="w-full h-full object-cover" />
                    : (p.name || "?").slice(0, 2)}
                </div>
                <div>
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-black/50">{p.description?.slice(0, 50)}</div>
                </div>
              </div>
            </td>

            <td className="p-3.5 text-xs">{p.code} <br /> {p.sku}</td>

            <td className="p-3.5 text-sm">
              {p.category_name} <br />
              <span className="text-xs text-black/50">{p.brand_name}</span>
            </td>

            <td className="p-3.5">₦{Number(p.cost || 0).toLocaleString()}</td>
            <td className="p-3.5 font-bold">₦{Number(p.price || 0).toLocaleString()}</td>

            <td className="p-3.5">
              <span className={low ? "text-rose-600 font-bold" : ""}>
                {p.stock}
              </span>
            </td>

            <td className="p-3.5">
              <span className={`text-xs px-2 py-1 rounded ${
                p.active ? "bg-[#E8F5E9] text-[#1FA868]" : "bg-black/10"
              }`}>
                {p.active ? "Active" : "Inactive"}
              </span>
            </td>

            <td className="p-3.5 text-right">
              <div className="flex justify-end gap-2">
                <Link to={`/t/${subdomain}/products/${p.id}/edit`}>
                  <Pencil size={14} />
                </Link>
                <button onClick={() => remove(p)}>
                  <Trash2 size={14} />
                </button>
              </div>
            </td>

          </tr>
        );
      })}
    </tbody>
  </table>
</div>


{/* 📱 MOBILE CARDS */}
<div className="md:hidden space-y-3 p-3">
  {rows.map((p) => {
    const low = (p.stock || 0) <= (p.stock_alert || 0);

    return (
      <div key={p.id} className="bg-white border border-black/5 rounded-xl p-4 shadow-sm">

        {/* Top */}
        <div className="flex gap-3">
          <div className="w-12 h-12 rounded-lg bg-[#E8F5E9] flex items-center justify-center overflow-hidden">
            {p.image_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.image_url}`} className="w-full h-full object-cover" />
              : (p.name || "?").slice(0, 2)}
          </div>

          <div className="flex-1">
            <div className="font-semibold text-black">{p.name}</div>
            <div className="text-xs text-black/50">{p.category_name} • {p.brand_name}</div>
          </div>
        </div>

        {/* Info */}
        <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
          <div>
            <div className="text-xs text-black/50">Price</div>
            <div className="font-bold">₦{Number(p.price || 0).toLocaleString()}</div>
          </div>

          <div>
            <div className="text-xs text-black/50">Stock</div>
            <div className={low ? "text-rose-600 font-bold" : ""}>
              {p.stock}
            </div>
          </div>

          <div>
            <div className="text-xs text-black/50">Code</div>
            <div className="text-xs">{p.code || "—"}</div>
          </div>

          <div>
            <div className="text-xs text-black/50">Status</div>
            <div className={`text-xs font-bold ${
              p.active ? "text-[#1FA868]" : "text-black/50"
            }`}>
              {p.active ? "Active" : "Inactive"}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-4">
          <Link to={`/t/${subdomain}/products/${p.id}/edit`}>
            <button className="p-2 rounded-md bg-[#E8F5E9] text-[#1FA868]">
              <Pencil size={14} />
            </button>
          </Link>

          <button
            onClick={() => remove(p)}
            className="p-2 rounded-md bg-rose-50 text-rose-600"
          >
            <Trash2 size={14} />
          </button>
        </div>

      </div>
    );
  })}
</div>

</Card>
    </div>
  );
}
